import { useEffect, useState } from "react";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import { Grid, TextField, Typography } from "@mui/material";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import StackKit from "@/shared/components/kit/stack";
import BoxKit from "@/shared/components/kit/box";
import CachedOutlinedIcon from "@mui/icons-material/CachedOutlined";
import Content from "./content";
import CustomTable from "@/shared/components/common/table";
import CustomButton from "@/shared/components/kit/button";
import ImgStaticCard from "@/shared/components/common/imgStaticCard/imgStaticCard";
import {numberSeparator,toPersianNumber,} from "@/hooks/useThousandsSeparator";
import CardBoxLayout from "../../layout/cardBoxLayout";
import { useNavigate } from "react-router";
import { fetchListGetBalancePazirande } from "@/api/merchant/request.get";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { fetchListChargeWalletPazirande2 } from "@/api/merchant/request.post";
import { toast } from "react-toastify";
import moment from "jalali-moment";

/**

* Finance
*
* کامپوننت صفحه افزایش موجودی نقدی و مشاهده تراکنش‌ها.
*
* ویژگی‌ها:
* * نمایش موجودی کل کیف پول نقدی کاربر.
* * نمایش آخرین تراکنش‌های شارژ موجودی به صورت جدول.
* * امکان جستجو در تراکنش‌ها.
* * نمایش کارت تصویری با استفاده از ImgStaticCard.
* * دکمه مشاهده گزارش ها کیف پول نقدی و بروزرسانی موجودی.
* * طراحی ریسپانسیو با استفاده از Grid و StackKit برای موبایل و دسکتاپ.
*
* داده‌ها:
* * fetchListGetBalancePazirande: اطلاعات موجودی کل کاربر
* * fetchListChargeWalletPazirande2: اطلاعات تراکنش‌ها و شارژ کیف پول
*
* @returns JSX.Element صفحه افزایش موجودی و تراکنش‌ها
  */

const Finance = () => {
  const navigation = useNavigate();
  const [depositWallet, setDepositWallet] = useState<any>();

  const {data: dataBalancePazirande,isFetched,isLoading,} = useQuery({
    queryKey: ["BalancePazirande-data"],
    queryFn: fetchListGetBalancePazirande,
  });

  const { mutate: submitFormDepositRequestPazirande } = useMutation({
    mutationFn: fetchListChargeWalletPazirande2,
    onSuccess: (data: any) => {
      // setListIncreaseGiftChart(data?.data?.chart)
      setDepositWallet(data?.data);
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(error?.response?.data?.message || "خطا در ثبت اطلاعات");
    },
  });

  useEffect(() => {
    submitFormDepositRequestPazirande({ text: "" });
  }, []);

  const dataHeader = [
    { id: "rowId", title: "ردیف" },
    { id: "price", title: "مبلغ(ریال)" },
    { id: "created_at", title: "ساعت و تاریخ" },
    { id: "source", title: "مبدا تراکنش" },
    { id: "target", title: "مقصد تراکنش" },
    { id: "ref", title: "کد رهگیری تراکنش" },
    { id: "description", title: "توضیحات" },
  ];

  const dataNew = depositWallet?.withdraws?.map((item: any, index: number) => {
    return {
      rowId: toPersianNumber(index + 1),
      ...item,
      price: numberSeparator(item.price),
      // type:refTypeMap[item.type] || item.type,
      created_at: ` ${toPersianNumber(
        moment.unix(item.created_at).locale("fa").format("HH:MM")
      )} ${toPersianNumber(
        moment.unix(item.created_at).locale("fa").format("YYYY/MM/DD")
      )}`,
      description: toPersianNumber(`${item.description}`),
      ref: toPersianNumber(`${item.ref}`),
      target: toPersianNumber(`${item.target}`),
    };
  });

  const fakeData = dataNew;
  
  return (
    <BoxKit sx={{display:'flex',flexDirection:'column',gap:2}}>
      <BoxKit sx={{ width: "100%", gap: 3 }}>
        {/* Header */}
        <TypoGraphyKit textAlign={'center'} alignItems={'center'} gap={1} display={'flex'} justifyContent={{sm:"flex-start" ,xs:'center'}} width={'100%'} variant="h5">
          <AccountBalanceWalletOutlinedIcon className="w-[40px] h-[40px]" />
          افزایش موجودی نقدی
        </TypoGraphyKit>

        {/* Balance Info */}
        <BoxKit display={'flex'} gap={2} sx={{margin:'1rem 0'}} flexDirection={{sm:'row',xs:'column'}} alignItems="center">
          <TypoGraphyKit variant="body1">
            موجودی کل کیف پول نقدی در حال حاضر
          </TypoGraphyKit>
          <StackKit flexDirection={{sm:'row',xs:'column'}} justifyContent="space-between" alignItems="center" gap={3}>
            <TypoGraphyKit variant="subtitle1">
              {numberSeparator(dataBalancePazirande?.balance)} ریال
            </TypoGraphyKit>
          </StackKit>
        </BoxKit>

        {/* Main Content + Image */}
        <Grid container spacing={2} direction={{xs:'column-reverse',sm:'row'}} justifyContent={{xs:'center',sm:'space-between'}} gap={2} alignItems="center">
          <Grid size={{xs:12,sm:8, md:8,}} >
            <Content />
          </Grid>
          <Grid size={{xs:12,sm:4, md:4,}} sx={{marginX:'auto'}} display="flex" justifyContent={'center'} height={'180px'} maxWidth={'180px'}>
            <ImgStaticCard widthBox="100%" img1Height="200px" img2Height="180px" imgSlider1="/card-out-simple-flat2-pullipay.svg" imgSlider2="/slide2-pullipay.svg" />
          </Grid>
        </Grid>
      </BoxKit>
      <CardBoxLayout>
        <StackKit sx={{ height: "100%" }} justifyContent={"space-between"}>
          {" "}
          <Grid container>
            <Grid size={12} padding={2}>
              <StackKit sx={{ width: "100%" }} alignItems={"start"}>
                <TypoGraphyKit variant="h6">
                  آخرین تراکنش های شارژ موجودی
                </TypoGraphyKit>
              </StackKit>
              <StackKit direction={"row"} justifyContent={"end"} sx={{ width: "100%" }} gap={2}>
                <TextField />
                <CustomButton sx={{ padding: "10px 25px" }}>
                  <Typography variant="body1">جستجو</Typography>
                </CustomButton>
              </StackKit>
            </Grid>
            <Grid size={12}>
              <CustomTable dataHeader={dataHeader} dataTbody={fakeData} />
            </Grid>
          </Grid>
          <StackKit sx={{ width: "100%" }} justifyContent={"end"} direction={"row"}>
            <CustomButton sx={{ bgcolor: "transparent", border: "1px dashed #EB6440", width: {xs:'100%', sm:"30%" , md:'20%'}, "&:hover": { bgcolor: "transparent", borderColor: "#EB6440", } }} onClick={() => navigation("/wallet-balance-reports")}>
              <TypoGraphyKit variant="body1" sx={{ color: "#EB6440" }}>
                مشاهده گزارش ها کیف پول نقدی
              </TypoGraphyKit>
            </CustomButton>
          </StackKit>
        </StackKit>
      </CardBoxLayout>
    </BoxKit>
  );
};

export default Finance;
